import React, { useState } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import 'flag-icons/css/flag-icons.min.css';

// Flag component using flag-icons CSS
const FlagIcon = ({ countryCode, className = '' }) => (
  <span className={`fi fi-${countryCode} ${className}`} style={{ fontSize: '1em', lineHeight: 1 }} />
);

// Supported languages for multi-language content
const CONTENT_LANGUAGES = [
  { code: 'en', name: 'English', countryCode: 'us' },
  { code: 'ja', name: '日本語', countryCode: 'jp' },
  { code: 'zh', name: '中文', countryCode: 'cn' },
  { code: 'vi', name: 'Tiếng Việt', countryCode: 'vn' },
  { code: 'id', name: 'Indonesia', countryCode: 'id' },
  { code: 'ms', name: 'Melayu', countryCode: 'my' },
  { code: 'ru', name: 'Русский', countryCode: 'ru' },
];

/**
 * MultiLanguageInput - Input component with tabs for multiple languages
 * 
 * @param {object} value - Object with language codes as keys { en: 'text', ja: 'テキスト', ... }
 * @param {function} onChange - Called with updated value object
 * @param {string} placeholder - Placeholder text (will be shown for each language)
 * @param {boolean} multiline - Use textarea instead of input
 * @param {number} rows - Number of rows for textarea
 * @param {string} className - Additional CSS classes
 * @param {string} label - Label text to show above the input
 */
export const MultiLanguageInput = ({
  value = {},
  onChange,
  placeholder = '',
  multiline = false,
  rows = 3,
  className = '',
  label = '',
}) => {
  const [activeLang, setActiveLang] = useState('en');
  const [isOpen, setIsOpen] = useState(false);

  // Ensure value is an object
  const safeValue = typeof value === 'object' && value !== null ? value : { en: value || '' };

  const handleChange = (text) => {
    const newValue = { ...safeValue, [activeLang]: text };
    onChange(newValue);
  };

  const getFilledLanguages = () => {
    return CONTENT_LANGUAGES.filter(lang => safeValue[lang.code]?.trim()).length;
  };

  const InputComponent = multiline ? Textarea : Input;

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="text-sm font-medium text-zinc-300">{label}</label>
      )}
      
      {/* Language Tabs */}
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center gap-1 bg-zinc-800/50 rounded-lg p-1 flex-wrap">
          {CONTENT_LANGUAGES.slice(0, 4).map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => setActiveLang(lang.code)}
              className={`px-2 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                activeLang === lang.code
                  ? 'bg-amber-600 text-white'
                  : safeValue[lang.code]?.trim()
                    ? 'bg-zinc-700 text-white'
                    : 'text-zinc-400 hover:text-zinc-300'
              }`}
            >
              <FlagIcon countryCode={lang.countryCode} /> {lang.code.toUpperCase()}
            </button>
          ))}
          
          {/* More languages dropdown */}
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={`px-2 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                  CONTENT_LANGUAGES.slice(4).some(l => l.code === activeLang)
                    ? 'bg-amber-600 text-white'
                    : 'text-zinc-400 hover:text-zinc-300'
                }`}
              >
                <Globe className="w-3 h-3" />
                <ChevronDown className="w-3 h-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-40 p-1 bg-zinc-800 border-zinc-700">
              {CONTENT_LANGUAGES.slice(4).map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => {
                    setActiveLang(lang.code);
                    setIsOpen(false);
                  }}
                  className={`w-full px-2 py-1.5 text-xs rounded-md transition-colors flex items-center gap-2 ${
                    activeLang === lang.code
                      ? 'bg-amber-600 text-white'
                      : safeValue[lang.code]?.trim()
                        ? 'bg-zinc-700 text-white'
                        : 'text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300'
                  }`}
                >
                  <FlagIcon countryCode={lang.countryCode} /> {lang.name}
                  {safeValue[lang.code]?.trim() && (
                    <span className="ml-auto text-green-400">✓</span>
                  )}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </div>
        
        {/* Filled count indicator */}
        <span className="text-xs text-zinc-500">
          {getFilledLanguages()}/{CONTENT_LANGUAGES.length}
        </span>
      </div>

      {/* Input Field */}
      <InputComponent
        value={safeValue[activeLang] || ''}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={`${placeholder} (${CONTENT_LANGUAGES.find(l => l.code === activeLang)?.name})`}
        className="bg-zinc-800 border-zinc-700 text-white"
        rows={multiline ? rows : undefined}
      />
      
      {/* Current language indicator */}
      <p className="text-xs text-zinc-500 flex items-center gap-1">
        <Globe className="w-3 h-3" />
        <FlagIcon countryCode={CONTENT_LANGUAGES.find(l => l.code === activeLang)?.countryCode} />{' '}
        {CONTENT_LANGUAGES.find(l => l.code === activeLang)?.name}
        {!safeValue[activeLang]?.trim() && activeLang !== 'en' && safeValue['en']?.trim() && (
          <span className="text-yellow-500 ml-2">• Will fallback to English</span>
        )}
      </p>
    </div>
  );
};

/**
 * Helper function to get localized text from multi-language object
 * Falls back to English if the requested language is not available
 */
export const getLocalizedText = (textObject, lang = 'en') => {
  if (!textObject) return '';
  if (typeof textObject === 'string') return textObject;
  return textObject[lang] || textObject['en'] || '';
};

export default MultiLanguageInput;
